'use strict';

// Dependencies
var SoaModel = require('bunsen/models/SoaModel');
var Listing = module.exports = SoaModel.extend();
var Publication = require('bunsen/models/inventory/Publication');
var endpoints = require('dibs-endpoints');
var NotesDocumentList = require('bunsen/models/NotesDocumentList');
var Note = require('bunsen/models/Note');
var Anchor = require('bunsen/models/Anchor');
var Backbone = require('dibs-backbone');
var PublicationType = require('bunsen/models/inventory/PublicationType');
var transitionMixin = require('bunsen/models/mixins/transitionMixin');
var AvailableActions = require('bunsen/collections/AvailableActions');
var _ = require('underscore');
var Deferred = require('simply-deferred');
var moment = require('moment');

/**
 * @class Listing
 * @extends SoaModel
 */

_.extend(Listing.prototype, {
    url : function () {
        return endpoints.singleListing(this.get('id'));
    },

    /**
     * Update bookmarked setting on listing.
     * If listing has one or more bookmark notes, the listing is considered bookmarked,
     * otherwise the listing is not bookmarked.
     * @param options - should we save change and what callbacks should be passed
     */
    toggleBookmark: function (options) {
        var existingBookmarkNotes = this.get('notes') &&
            this.get('notes').where({'type' : Note.TYPE_LISTING_BOOKMARK});
        var newBookmarkNoteOptions;
        var notesDeferreds = [];
        options = options || {};

        // if bookmarked then we need to un-bookmark, so delete notes
        if (existingBookmarkNotes && existingBookmarkNotes.length) {
            _.each(existingBookmarkNotes, function (note) {
                note.service = endpoints.latest.inventoryListings;
                notesDeferreds.push(note.destroy({queries : {
                    indexMode : "sync"
                }}));
            });

            return Deferred.when.apply(Deferred, notesDeferreds).done(function () {
                if (options.success) {
                    options.success();
                }
            }).fail(function () {
                if (options.error) {
                    options.error();
                }
            });
        } else {
            // listing is not bookmarked
            // add a note to switch this listing to be bookmarked
            newBookmarkNoteOptions = {
                service: endpoints.latest.inventoryListings,
                type: Note.TYPE_LISTING_BOOKMARK,
                user: options.user,
                anchors: [
                    {
                        sourceId: this.id,
                        sourceType: Anchor.TYPE_ITEM_LISTING
                    }
                ]
            };
            return this.getNotes().addNewNote(newBookmarkNoteOptions, _.extend(options, {queries : {
                indexMode : "sync"
            }}));
        }
    },

    canPromote: function () {
        var actions = this.get('actions');
        return !!(actions && actions.find(function (_a) {
            return _a.code === Listing.ACTIONS.PROMOTE_LISTING;
        }));
    },

    canDelay: function (userOrSeller) {
        var actions = this.get('actions');
        var actionsColl;
        if (actions && actions.length) {
            actionsColl = new AvailableActions(actions);
            return !!actionsColl.isUserAuthorizedFor(Listing.ACTIONS.DELAY, userOrSeller);
        }
        return false;
    },

    getPromoteDestinations: function () {
        return this.canPromote() && this.get('actions').find(function (_a) {
            return _a.code === Listing.ACTIONS.PROMOTE_LISTING;
        }).details.promotionDestinations || [];
    },

    ///////////////////
    // Type
    isStorefront: function () {
        return this.get('type').isStorefront();
    },
    isMarketplace: function () {
        return this.get('type').isMarketplace();
    },
    isFeatured: function () {
        return this.get('type').isFeatured();
    },
    isSale: function () {
        return this.get('type').isSale();
    },
    /**
     * Update bookmarked setting on listing.
     * If listing has one or more bookmark notes, the listing is considered bookmarked,
     * otherwise the listing is not bookmarked.
     * @returns {Boolean}
     */
    isBookmarked: function () {
        return !!(this.get('notes') && this.get('notes').findWhere({'type' : Note.TYPE_LISTING_BOOKMARK}));
    },

    ///////////////////
    // Statuses
    isPublishedStatus: function () {
        return this.get('status') === Listing.STATUS.PUBLISHED;
    },
    isNewStatus: function () {
        return this.get('status') === Listing.STATUS.NEW;
    },
    isQueuedStatus: function () {
        return this.get('status') === Listing.STATUS.QUEUED;
    },
    isCompleteStatus: function () {
        return this.get('status') === Listing.STATUS.COMPLETE;
    },
    isDeletedStatus: function () {
        return this.get('status') === Listing.STATUS.DELETED;
    },
    isNotDeletedStatus: function () {
        return this.get('status') !== Listing.STATUS.DELETED;
    },
    isUnavailableStatus: function () {
        return this.get('status') === Listing.STATUS.UNAVAILABLE;
    },

    ///////////////////
    // Setters
    markDeleted : function () {
        this.set('status', Listing.STATUS.DELETED);
    },
    setPublication: function (publication) {
        return this.set('publication', publication);
    },

    ///////////////////
    // Getters
    getPublication: function () {
        return this.get('publication');
    },
    getType: function () {
        return this.get('type');
    },
    getSort: function () {
        return this.get('sort');
    },

    /**
     * Retrieve notes, set an empty collection for Notes if none returned from fetch.
     * @returns {Notes}
     */
    getNotes: function () {
        var notes = this.get('notes');

        if (_.isUndefined(notes)) {
            this.set('notes', new NotesDocumentList({
                service: endpoints.latest.inventoryListings,
                documents: []
            }));
            notes = this.get('notes');
        }
        return notes;
    },

    /**
     * "Snooze" listing by updating eligibility date.
     * @returns {*|$.Deferred.promise}
     */
    delay: function () {
        var notes = this.get('notes');
        var oldEligibilityDate = this.get('eligibilityDate');
        var newEligibilityDate = moment(oldEligibilityDate).add(7, 'days').format('YYYY-MM-DDTHH:mm:ss.SSSZ');
        this.set('eligibilityDate', newEligibilityDate);

        if (notes) {
            notes.unset('service'); // not a valid field for BE, needs to be removed before save
        }

        return this.save(null, {
            method: 'PUT',
            queries : {
                indexMode : 'sync',
                workflowAction: 'update'
            }
        });
    },

    relations: [
        {
            key: 'item',
            type: Backbone.One,
            relatedModel: function () {
                return require('bunsen/models/inventory/Item-v3');
            }
        },
        {
            key : 'type',
            type: Backbone.One,
            relatedModel : PublicationType
        },
        {
            key: 'notes',
            type: Backbone.One,
            relatedModel: NotesDocumentList
        },
        {
            key: 'publication',
            type: Backbone.One,
            relatedModel: Publication
        }
    ],
    defaults : {
        apiType : 'itemListing'
    }
}, transitionMixin);


// static props :
_.extend(Listing, {
    TYPE: {
        FEATURED: 'FEATURED',
        MARKETPLACE: 'MARKETPLACE',
        STOREFRONT: 'DEALER_STOREFRONT',
        SATURDAY_SALE: 'SATURDAY_SALE'
    },
    STATUS: {
        NEW: 'NEW',
        COMPLETE: 'COMPLETE',
        QUEUED: 'QUEUED',
        PUBLISHED: 'PUBLISHED',
        UNAVAILABLE: 'UNAVAILABLE',
        DELETED: 'DELETED'
    },
    REASON_CODE: {
        UNLISTED: 'UNLISTED',
        SOLD: 'SOLD',
        SOLD_HIDDEN_MANUALLY : 'SOLD_HIDDEN_MANUALLY',
        DEMOTED: 'DEMOTED',
        ITEM_MOVED_TO_INCOMPLETE: 'ITEM_MOVED_TO_INCOMPLETE',
        ITEM_DELETED: 'ITEM_DELETED',
        REPOSTED: 'REPOSTED',
        CLEANED_UP_AFTER_ERROR: 'CLEANED_UP_AFTER_ERROR',
        DELETED_BY_MIGRATION: 'DELETED_BY_MIGRATION',
        EXPIRED: 'EXPIRED',
        ON_HOLD: 'ON_HOLD',
        EXPIRED_SOLD: 'EXPIRED_SOLD',
        INELIGIBLE: 'INELIGIBLE'
    },
    ACTIONS: {
        PROMOTE_LISTING: 'PROMOTE_LISTING',
        DELAY: 'DELAY'
    }
});



//////////////////
// WEBPACK FOOTER
// ./~/bunsen/models/inventory/Listing.js
// module id = 1465
// module chunks = 63